'use client'

import { FeatureFlag, useFeatureFlag } from '@/config/featureFlags'
import Cards from '@/ui/assets/components/cards.inline.svg'
import { Button } from '@/ui/atoms/button/Button'
import { Panel } from '@/ui/atoms/panel/Panel'
import { Typography } from '@/ui/atoms/typography/Typography'
import { cn } from '@/ui/utils/style'
import { ArrowRight, CheckCircle2, Crown, RefreshCw, TrendingUp, Users } from 'lucide-react'
import { KYCStatus, WaitlistEntry } from '../types/CardState'
import { CardReferralShare } from './CardReferralShare'

interface CardPriorityAccessProps {
  waitlistEntry?: WaitlistEntry
  kycStatus?: KYCStatus
  onStartKYC: () => void
  onCheckStatus?: () => void
  isCheckingStatus?: boolean
}

export function CardPriorityAccess({
  waitlistEntry,
  kycStatus,
  onStartKYC,
  onCheckStatus,
  isCheckingStatus,
}: CardPriorityAccessProps) {
  const showReferrals = useFeatureFlag(FeatureFlag.SHOW_WAITLIST_REFERRALS)
  const isKYCApproved = kycStatus === KYCStatus.APPROVED
  const isKYCInProgress =
    kycStatus === KYCStatus.PENDING || kycStatus === KYCStatus.REVIEW || kycStatus === KYCStatus.INITIATED
  const hasSubmittedKYC = isKYCApproved || isKYCInProgress
  return (
    <div className="flex flex-col items-center gap-12 lg:flex-row lg:items-start">
      <div className="flex flex-1 flex-col gap-6">
        <Panel.Wrapper className="relative w-full max-w-xl overflow-hidden">
          <div className="pointer-events-none absolute inset-0 rounded-lg bg-gradient-to-br from-gradient-yellow/15 via-transparent to-primary/15" />

          <Panel.Content className="relative flex flex-col gap-6 p-8">
            <div className="flex items-center gap-3">
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-gradient-to-br from-gradient-yellow to-primary">
                <Crown className="h-6 w-6 text-black" />
              </div>
              <div>
                <Typography variant="h3" gradient>
                  {isKYCApproved
                    ? 'KYC Approved'
                    : isKYCInProgress
                      ? 'KYC Verification In Progress'
                      : 'Priority Access Granted'}
                </Typography>
              </div>
            </div>

            <Typography variant="p" className="text-muted-foreground">
              {isKYCApproved
                ? 'Your identity verification is approved. Check your status to see if your card access has been activated.'
                : isKYCInProgress
                  ? 'Your identity verification has been submitted and is being reviewed. Check your status for updates.'
                  : 'You\u2019re eligible for the HypurrFi Card. Verify your identity to activate your card.'}
            </Typography>

            <div className="flex flex-col gap-3 rounded-lg border border-border/50 bg-background/30 p-4">
              <div className="flex items-center gap-2">
                <CheckCircle2 className="h-4 w-4 text-primary" />
                <Typography variant="p" className="text-foreground text-sm">
                  {waitlistEntry ? 'Eligibility verified — priority access granted' : 'Eligibility verified'}
                </Typography>
              </div>
              <div className="flex items-center gap-2">
                {isKYCApproved ? (
                  <CheckCircle2 className="h-4 w-4 text-primary" />
                ) : isKYCInProgress ? (
                  <div className="h-4 w-4 animate-spin rounded-full border-2 border-primary/20 border-t-primary" />
                ) : (
                  <div className="h-4 w-4 rounded-full border-2 border-muted-foreground/50 border-dashed" />
                )}
                <Typography
                  variant="p"
                  className={cn('text-sm', hasSubmittedKYC ? 'text-foreground' : 'text-muted-foreground')}
                >
                  {isKYCApproved
                    ? 'KYC verification approved'
                    : isKYCInProgress
                      ? 'KYC verification in progress'
                      : 'Complete KYC verification'}
                </Typography>
              </div>
              <div className="flex items-center gap-2">
                <div className="h-4 w-4 rounded-full border-2 border-muted-foreground/50 border-dashed" />
                <Typography variant="p" className="text-muted-foreground text-sm">
                  Card activation
                </Typography>
              </div>
            </div>

            {showReferrals && waitlistEntry && (
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                <div className="rounded-lg border border-border/60 bg-background/30 p-4">
                  <Typography variant="prompt" className="text-muted-foreground">
                    Position
                  </Typography>
                  <div className="flex items-center gap-2">
                    <Users className="h-4 w-4 text-primary" />
                    <Typography variant="h3">#{waitlistEntry.position}</Typography>
                  </div>
                </div>

                <div className="rounded-lg border border-border/60 bg-background/30 p-4">
                  <Typography variant="prompt" className="text-muted-foreground">
                    Referrals
                  </Typography>
                  <Typography variant="h3">{waitlistEntry.referralCount}</Typography>
                </div>

                <div className="rounded-lg border border-border/60 bg-background/30 p-4">
                  <Typography variant="prompt" className="text-muted-foreground">
                    Boost Score
                  </Typography>
                  <div className="flex items-center gap-2">
                    <TrendingUp className="h-4 w-4 text-primary" />
                    <Typography variant="h3">{waitlistEntry.boostScore}</Typography>
                  </div>
                </div>
              </div>
            )}

            {hasSubmittedKYC ? (
              <Button
                onClick={onCheckStatus}
                disabled={isCheckingStatus}
                size="lg"
                variant="outline"
                className="w-full transition-all duration-300 hover:border-primary/50 hover:bg-primary/5"
                prefixIcon={
                  isCheckingStatus ? <RefreshCw className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />
                }
              >
                {isCheckingStatus ? 'Checking Status...' : 'Check Status'}
              </Button>
            ) : (
              <Button
                onClick={onStartKYC}
                size="lg"
                className={cn(
                  'w-full bg-gradient-to-r from-gradient-yellow to-gradient-green text-black',
                  'transition-all duration-300 hover:opacity-90 hover:shadow-lg hover:shadow-primary/20',
                )}
                postfixIcon={<ArrowRight className="h-5 w-5" />}
              >
                Start KYC Verification
              </Button>
            )}

            {showReferrals && waitlistEntry && <CardReferralShare referralCode={waitlistEntry.referralCode} />}
          </Panel.Content>
        </Panel.Wrapper>
      </div>

      <div className="relative flex items-center justify-center lg:flex-1">
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="h-[250px] w-[350px] rounded-full bg-gradient-to-br from-gradient-yellow/15 to-primary/15 blur-[60px]" />
        </div>
        <div className="relative drop-shadow-[0_0_40px_rgba(251,229,114,0.15)] transition-transform duration-500 hover:scale-105">
          <Cards className="h-auto w-full max-w-sm" />
        </div>
      </div>
    </div>
  )
}
